import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

/* Entity Intelligence Platform — canonical registry client.
   Identity separation is a hard rule: Aave Protocol ≠ AAVE Token,
   Ethereum Network ≠ ETH Asset, Tether (company) ≠ USDT (asset). */

export type EntityType = "protocol" | "chain" | "token" | "company" | "project";

export interface EntityRecord {
  id: string;
  type: EntityType;
  slug: string;
  name: string;
  symbol?: string | null;
  live: boolean;
  route: string | null;
  logo?: string | null;
}

export interface EntityRegistry {
  types: EntityType[];
  route_conventions: Record<EntityType, string>;
  entities: EntityRecord[];
  relationships: { from: string; rel: string; to: string }[];
}

export const useEntityRegistry = () =>
  useQuery<EntityRegistry>({
    queryKey: ["entity-registry"],
    queryFn: async () => (await api.get("/entities/registry")).data,
    staleTime: 60 * 60 * 1000,
  });

export const resolveEntity = (
  reg: EntityRegistry | undefined, type: string, slug: string,
): EntityRecord | null =>
  reg?.entities?.find((e) => e.type === type && e.slug === slug) || null;

/* STRICT navigation truth (Entity Discovery UX v1, Founder Rule 6):
   a public entity link exists ONLY when the registry says live && route.
   Non-live identities render as informational content — no fake affordance,
   no silent search redirect. Flipping `live` activates links platform-wide. */
export const entityNavRoute = (e: EntityRecord | null): string | null =>
  e?.live && e.route ? e.route : null;

export const useEntityNav = (type: EntityType, slug?: string | null) => {
  const { data } = useEntityRegistry();
  const entity = slug ? resolveEntity(data, type, slug) : null;
  const route = entityNavRoute(entity);
  return { entity, live: !!route, route };
};

/* Provider child slugs (e.g. DeFiLlama "aave-v3") → canonical parent slug.
   Registry-gated: strips a provider -vN suffix ONLY when the parent exists
   as a canonical entity. Never name-similarity inference. */
export const canonicalProtocolSlug = (
  reg: EntityRegistry | undefined, slug?: string | null,
): string | null => {
  if (!reg || !slug) return null;
  if (resolveEntity(reg, "protocol", slug)) return slug;
  const parent = slug.replace(/-v\d+$/, "");
  return parent !== slug && resolveEntity(reg, "protocol", parent) ? parent : null;
};

/* Coin → Protocol bridge: verified deterministic registry relationship
   (token_of) only. Never inferred from ticker/name similarity. */
export const useTokenProtocolBridge = (symbol?: string | null) => {
  const { data } = useEntityRegistry();
  const sym = (symbol || "").toUpperCase();
  const token = sym
    ? data?.entities?.find((e) => e.type === "token" && (e.symbol || "").toUpperCase() === sym) || null
    : null;
  const edge = token
    ? data?.relationships?.find((r) => r.from === token.id && r.rel === "token_of") || null
    : null;
  const protocol = edge ? data?.entities?.find((e) => e.id === edge.to) || null : null;
  return { token, protocol, route: entityNavRoute(protocol) };
};

/* ---------- Entity Data Service hooks (normalized NewsLayer payloads only —
   components never call DeFiLlama/Kraken directly) ---------- */

export const useProtocolOverview = (slug?: string) =>
  useQuery({
    queryKey: ["entity-overview", "protocol", slug],
    queryFn: async () => (await api.get(`/entities/protocol/${slug}/overview`)).data,
    enabled: !!slug,
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000, // live metric transitions (Motion Contract §6)
    refetchIntervalInBackground: false, // no motion/work while document hidden (§8)
  });

export const useProtocolTvl = (slug?: string, range: string = "30d") =>
  useQuery({
    queryKey: ["entity-tvl", "protocol", slug, range],
    queryFn: async () => (await api.get(`/entities/protocol/${slug}/tvl`, { params: { range } })).data,
    enabled: !!slug,
    staleTime: 10 * 60 * 1000,
    placeholderData: (prev: any) => prev, // TRANSITION motion: ranges morph, never blank
  });

/* ---------- Entity analytics (existing /analytics/track architecture) ---------- */

export const entityTrack = (
  event: "entity_page_view" | "related_entity_click" | "token_click" | "chain_click"
    | "entity_nav_click" | "article_click" | "chart_range_change" | "network_node_focus"
    | "governance_proposal_click" | "security_incident_click"
    | "regulation_item_click" | "layer_index_click"
    | "entity_explorer_search" | "entity_explorer_filter",
  entityId: string, metadata: Record<string, unknown> = {},
) => {
  import("@/hooks/useArticleAnalytics").then(({ trackEvent }) =>
    trackEvent(event, { entity_id: entityId, ...metadata }));
};

/* ---------- Entity SEO (approved model: BreadcrumbList + WebPage about:Thing —
   never Corporation/FinancialProduct for a protocol) ---------- */

export const buildProtocolJsonLd = (identity: {
  name: string; slug: string; description?: string | null;
  url?: string | null; logo?: string | null;
}, siteUrl: string) => {
  const page = `${siteUrl}/protocols/${identity.slug}`;
  return [
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Sectors", item: `${siteUrl}/defi` },
        { "@type": "ListItem", position: 2, name: "DeFi", item: `${siteUrl}/defi` },
        { "@type": "ListItem", position: 3, name: "Protocols", item: `${siteUrl}/defi/protocols` },
        { "@type": "ListItem", position: 4, name: identity.name, item: page },
      ],
    },
    {
      "@context": "https://schema.org", "@type": "WebPage",
      url: page,
      name: `${identity.name} Protocol Intelligence`,
      about: {
        "@type": "Thing", name: identity.name,
        description: identity.description || undefined,
        url: identity.url || undefined,
        image: identity.logo || undefined,
        sameAs: identity.url ? [identity.url] : undefined,
      },
    },
  ];
};
